import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { adminApi, type AdvertiserStatus } from '../../services/adminApi';

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

const STATUS_COLOR: Record<AdvertiserStatus, 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  active: 'success',
  suspended: 'default',
};

/**
 * Admin advertiser detail page (GitHub #12 area 3).
 *
 * Shows a single advertiser account: company / contact details, lifecycle
 * status with approve / suspend controls, the account's campaigns, and an
 * aggregate billing summary (invoice counts and totals). Auth is enforced
 * upstream by AdminLayout.
 *
 * Privacy / scope (spec §5/§17.3): account administration only. Billing here is
 * aggregate totals over existing invoices — no payment card data, no stored
 * tokens are ever displayed.
 */
export function AdvertiserDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'advertisers', id],
    queryFn: () => adminApi.getAdvertiser(id),
    enabled: Boolean(id),
  });

  const mutation = useMutation({
    mutationFn: (status: AdvertiserStatus) => adminApi.patchAdvertiser(id, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'advertisers'] });
    },
  });

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/admin/advertisers"
        sx={{ display: 'inline-block', mb: 1 }}
      >
        ← Back to advertisers
      </MuiLink>

      {mutation.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Update failed: {(mutation.error as Error).message}
        </Alert>
      )}

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading advertiser" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load advertiser: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h1" sx={{ fontSize: '1.6rem' }}>
              {query.data.advertiser.companyName}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={query.data.advertiser.status}
                color={STATUS_COLOR[query.data.advertiser.status]}
                sx={{ textTransform: 'capitalize' }}
              />
              {query.data.advertiser.status === 'pending' && (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate('active')}
                  aria-label={`Approve ${query.data.advertiser.companyName}`}
                >
                  Approve
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                color={query.data.advertiser.status === 'suspended' ? 'success' : 'warning'}
                disabled={mutation.isPending}
                onClick={() =>
                  mutation.mutate(
                    query.data.advertiser.status === 'suspended' ? 'active' : 'suspended'
                  )
                }
                aria-label={`${
                  query.data.advertiser.status === 'suspended' ? 'Reactivate' : 'Suspend'
                } ${query.data.advertiser.companyName}`}
              >
                {query.data.advertiser.status === 'suspended' ? 'Reactivate' : 'Suspend'}
              </Button>
            </Stack>
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {query.data.advertiser.contactEmail}
          </Typography>

          {/* ── Billing summary (aggregate totals only — no card data) ── */}
          <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
            Billing summary
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 4 }}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                  {query.data.billing.invoiceCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invoices
                </Typography>
              </Box>
              <Box>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                  {dollars(query.data.billing.totalBilledCents)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total billed
                </Typography>
              </Box>
              <Box>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                  {dollars(query.data.billing.outstandingCents)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Outstanding
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* ── Campaigns ── */}
          <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
            Campaigns
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table aria-label="Advertiser campaigns">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Daily budget</TableCell>
                  <TableCell>Total budget</TableCell>
                  <TableCell>Spent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        This advertiser has no campaigns.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.campaigns.map((campaign) => (
                    <TableRow key={campaign.id} hover>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{campaign.status}</TableCell>
                      <TableCell>{dollars(campaign.dailyBudgetCents)}</TableCell>
                      <TableCell>{dollars(campaign.totalBudgetCents)}</TableCell>
                      <TableCell>{dollars(campaign.spentCents)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Invoices ── */}
          <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
            Invoices
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Advertiser invoices">
              <TableHead>
                <TableRow>
                  <TableCell>Period start</TableCell>
                  <TableCell>Period end</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.billing.invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No invoices on record.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.billing.invoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>{invoice.periodStart}</TableCell>
                      <TableCell>{invoice.periodEnd}</TableCell>
                      <TableCell>{dollars(invoice.totalCents)}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{invoice.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

export default AdvertiserDetailPage;
